import { useState, useEffect, useCallback } from "react";
import { Volume2, RefreshCw } from "lucide-react";
import { earnXP } from "../gamification";
import { OBJECTS, SUBJECTS, articleFor, generateSentence, PATTERNS } from "../lib/sentenceGenerator";
import { playCorrect, playWrong, playLevelUp } from "../sounds";

function pick(arr) { return arr[Math.floor(Math.random() * arr.length)]; }
function pickN(arr, n) { return [...arr].sort(() => Math.random() - 0.5).slice(0, n); }
function speak(text, opts = {}) {
  if (!("speechSynthesis" in window)) return;
  window.speechSynthesis.cancel();
  const u = new SpeechSynthesisUtterance(text);
  u.lang = "en-US"; u.rate = opts.rate || 0.85;
  window.speechSynthesis.speak(u);
}

// ============================================================
// GAME 1: VOWEL CATCHER — falling letters, tap vowels only
// ============================================================
export function VowelCatcher({ onFinish }) {
  const [items, setItems] = useState([]); // {id, letter, x, y, vowel}
  const [score, setScore] = useState(0);
  const [misses, setMisses] = useState(0);
  const [time, setTime] = useState(30);
  const [running, setRunning] = useState(true);

  useEffect(() => {
    if (!running) return;
    const spawn = setInterval(() => {
      const allLetters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
      const letter = pick(allLetters);
      setItems((prev) => [...prev, {
        id: `${Date.now()}-${Math.random()}`,
        letter,
        x: 5 + Math.random() * 85,
        y: -10,
        vowel: ["A", "E", "I", "O", "U"].includes(letter),
      }]);
    }, 700);
    return () => clearInterval(spawn);
  }, [running]);

  useEffect(() => {
    if (!running) return;
    const fall = setInterval(() => {
      setItems((prev) =>
        prev
          .map((it) => ({ ...it, y: it.y + 3 }))
          .filter((it) => {
            if (it.y > 100) {
              if (it.vowel) setMisses((m) => m + 1);
              return false;
            }
            return true;
          })
      );
    }, 60);
    return () => clearInterval(fall);
  }, [running]);

  useEffect(() => {
    if (!running) return;
    const tick = setInterval(() => {
      setTime((t) => {
        if (t <= 1) {
          setRunning(false);
          earnXP(score, "game");
          onFinish?.(score);
          return 0;
        }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(tick);
  }, [running, score, onFinish]);

  function tap(item) {
    setItems((p) => p.filter((i) => i.id !== item.id));
    if (item.vowel) {
      setScore((s) => s + 10);
      try { playCorrect(); } catch {}
      speak(item.letter);
    } else {
      setMisses((m) => m + 1);
      try { playWrong(); } catch {}
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-sm font-bold">⏱️ {time}s</div>
        <div className="text-sm font-bold text-sage-dark">⭐ {score} XP</div>
        <div className="text-sm font-bold text-rust-dark">❌ {misses}/5</div>
      </div>
      <div className="text-center text-xs text-gray-600">Tap only the VOWELS! (A · E · I · O · U)</div>
      <div className="relative w-full h-[400px] bg-gradient-to-b from-sky-100 via-sky-200 to-sage-light dark:from-sky-900 dark:via-ink-800 dark:to-gold-600 rounded-3xl overflow-hidden border-4 border-gold-500">
        {items.map((it) => (
          <button
            key={it.id}
            onClick={() => tap(it)}
            style={{ left: `${it.x}%`, top: `${it.y}%` }}
            className={`absolute -translate-x-1/2 w-12 h-12 rounded-full font-black text-xl shadow-lg transition-transform hover:scale-125 ${
              it.vowel
                ? "bg-gradient-to-br from-gold-300 to-gold-600 text-gold-700 ring-2 ring-gold-100"
                : "bg-gradient-to-br from-ink-100 to-sky-dark text-white"
            }`}
          >
            {it.letter}
          </button>
        ))}
        {misses >= 5 && running && setTimeout(() => { setRunning(false); onFinish?.(score); }, 0)}
      </div>
    </div>
  );
}

// ============================================================
// GAME 2: ARTICLE QUIZ — pick "a" or "an" rapid-fire
// ============================================================
export function ArticleQuiz({ onFinish }) {
  const [round, setRound] = useState(0);
  const [score, setScore] = useState(0);
  const [combo, setCombo] = useState(0);
  const [feedback, setFeedback] = useState(null);
  const TOTAL = 10;
  const [word, setWord] = useState(() => pick([...OBJECTS, ...SUBJECTS.filter((s) => s.isNoun)]));

  function pickArticle(art) {
    if (feedback) return;
    const correct = articleFor(word.word);
    const isRight = art === correct;
    if (isRight) {
      setScore((s) => s + 10 + combo * 2);
      setCombo((c) => c + 1);
      setFeedback("right");
      try { playCorrect(); } catch {}
      speak(`${correct} ${word.word}`);
    } else {
      setCombo(0);
      setFeedback("wrong");
      try { playWrong(); } catch {}
    }
    setTimeout(() => {
      const newRound = round + 1;
      if (newRound >= TOTAL) {
        try { playLevelUp(); } catch {}
        earnXP(score, "game");
        onFinish?.(Math.round(score / 10));
        return;
      }
      setRound(newRound);
      setWord(pick([...OBJECTS, ...SUBJECTS.filter((s) => s.isNoun)]));
      setFeedback(null);
    }, 900);
  }

  return (
    <div className="space-y-5 text-center">
      <div className="flex items-center justify-between">
        <span className="text-xs font-bold text-gray-500">Round {round + 1}/{TOTAL}</span>
        {combo >= 2 && (
          <span className="px-2 py-1 rounded-full bg-gold-500 text-white text-xs font-black animate-pulse">🔥 {combo}× combo</span>
        )}
        <span className="text-xs font-bold text-gold-700">⭐ {score} XP</span>
      </div>

      <p className="text-sm text-gray-600 dark:text-gray-400">Pick the right article for:</p>
      <div className="text-7xl mb-2">{word.emoji || "📦"}</div>
      <p className="text-3xl font-black text-gray-900 dark:text-white">{word.word}</p>

      <div className="grid grid-cols-2 gap-3">
        {["a", "an"].map((a) => (
          <button
            key={a}
            onClick={() => pickArticle(a)}
            disabled={!!feedback}
            className={`py-6 rounded-2xl text-3xl font-black shadow-lg transition-transform hover:scale-105 ${
              feedback === "right" && a === articleFor(word.word) ? "bg-sage text-white" :
              feedback === "wrong" && a !== articleFor(word.word) ? "bg-rust text-white animate-shake" :
              "bg-white dark:bg-ink-800 border-2 border-gold-500 hover:border-gold-500 text-gold-700 dark:text-paper"
            }`}
          >
            {a}
          </button>
        ))}
      </div>

      {feedback === "right" && <p className="text-sage-dark font-bold">✅ {articleFor(word.word)} {word.word}</p>}
      {feedback === "wrong" && <p className="text-rust-dark font-bold">❌ It's "{articleFor(word.word)} {word.word}"</p>}
    </div>
  );
}

// ============================================================
// GAME 3: ANIMAL SENTENCE BUILDER — drag animal/verb/object emojis
// ============================================================
export function AnimalSentence({ onFinish }) {
  const [sentence, setSentence] = useState(() => generateSentence(PATTERNS.SVO));
  const [shuffled, setShuffled] = useState([]);
  const [slots, setSlots] = useState([]);
  const [round, setRound] = useState(1);
  const [score, setScore] = useState(0);
  const TOTAL = 5;

  useEffect(() => {
    const s = sentence.words.map((w, i) => ({ ...w, id: `${i}-${Math.random()}` }));
    setShuffled([...s].sort(() => Math.random() - 0.5));
    setSlots([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sentence]);

  function place(word) {
    setShuffled((p) => p.filter((w) => w.id !== word.id));
    setSlots((p) => [...p, word]);
    speak(word.text);
  }

  function check() {
    const userText = slots.map((s) => s.text).join(" ");
    if (userText.toLowerCase() === sentence.text.toLowerCase()) {
      setScore((s) => s + 1);
      try { playCorrect(); } catch {}
      speak(sentence.text);
      setTimeout(() => {
        if (round >= TOTAL) {
          earnXP(score * 10 + 10, "game");
          onFinish?.(score + 1);
        } else {
          setRound((r) => r + 1);
          setSentence(generateSentence(PATTERNS.SVO));
        }
      }, 1200);
    } else {
      try { playWrong(); } catch {}
      const ws = sentence.words.map((w, i) => ({ ...w, id: `${i}-${Math.random()}` }));
      setShuffled([...ws].sort(() => Math.random() - 0.5));
      setSlots([]);
    }
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <span className="text-xs font-bold text-gray-500">Round {round}/{TOTAL}</span>
        <span className="text-xs font-bold text-sage-dark">⭐ {score} correct</span>
      </div>

      <p className="text-center text-sm text-gray-600 dark:text-gray-400">Make the funny sentence!</p>

      <div className="min-h-[100px] p-4 rounded-2xl border-4 border-dashed border-rust-light bg-rust-light dark:bg-rust-dark/20 flex flex-wrap gap-3 items-center justify-center">
        {slots.length === 0 && <p className="text-sm text-gray-400 italic">⬇️ Tap emojis below</p>}
        {slots.map((w) => (
          <button key={w.id} onClick={() => { setSlots((p) => p.filter((x) => x.id !== w.id)); setShuffled((p) => [...p, w]); }} className="text-4xl bg-white dark:bg-ink-800 rounded-2xl p-2 shadow-md hover:scale-110 transition">
            {w.emoji} <span className="block text-xs font-bold">{w.text}</span>
          </button>
        ))}
      </div>

      <div className="flex flex-wrap gap-2 justify-center">
        {shuffled.map((w) => (
          <button key={w.id} onClick={() => place(w)} className="text-3xl bg-gradient-to-br from-gold-100 to-gold-100 rounded-2xl p-3 shadow-md border-2 border-gold-300 hover:scale-110 transition">
            {w.emoji}<span className="block text-xs font-bold text-gray-700">{w.text}</span>
          </button>
        ))}
      </div>

      {shuffled.length === 0 && (
        <button onClick={check} className="w-full bg-gradient-to-r from-rust to-gold-600 text-white font-black py-3 rounded-2xl shadow-lg hover:scale-[1.02] transition">
          Check ✨
        </button>
      )}
    </div>
  );
}

// ============================================================
// GAME 4: SOUND MATCH — hear word, click matching emoji
// ============================================================
export function SoundMatch({ onFinish }) {
  const [round, setRound] = useState(0);
  const [score, setScore] = useState(0);
  const [target, setTarget] = useState(null);
  const [opts, setOpts] = useState([]);
  const [feedback, setFeedback] = useState(null);
  const TOTAL = 8;
  const pool = OBJECTS.filter((o) => o.kid && o.emoji);

  const newRound = useCallback(() => {
    const t = pick(pool);
    const distractors = pickN(pool.filter((p) => p.word !== t.word), 3);
    setTarget(t);
    setOpts([t, ...distractors].sort(() => Math.random() - 0.5));
    setFeedback(null);
    setTimeout(() => speak(t.word), 300);
  }, [pool]);

  useEffect(() => { newRound(); }, []); // eslint-disable-line

  function tap(opt) {
    if (feedback) return;
    if (opt.word === target.word) {
      setScore((s) => s + 1);
      setFeedback("right");
      try { playCorrect(); } catch {}
    } else {
      setFeedback("wrong");
      try { playWrong(); } catch {}
    }
    setTimeout(() => {
      const next = round + 1;
      if (next >= TOTAL) {
        earnXP(score * 8, "game");
        onFinish?.(score + (opt.word === target.word ? 1 : 0));
      } else {
        setRound(next);
        newRound();
      }
    }, 1000);
  }

  return (
    <div className="space-y-5 text-center">
      <div className="flex items-center justify-between">
        <span className="text-xs font-bold text-gray-500">Round {round + 1}/{TOTAL}</span>
        <span className="text-xs font-bold text-sage-dark">⭐ {score}</span>
      </div>
      <p className="text-sm text-gray-600 dark:text-gray-400">Listen and tap the right picture</p>
      <button onClick={() => target && speak(target.word)} className="inline-flex items-center gap-2 bg-ink-900 hover:bg-ink-900 text-white font-bold px-5 py-3 rounded-full shadow-lg hover:scale-105 transition">
        <Volume2 className="w-5 h-5" /> Hear it
      </button>
      <div className="grid grid-cols-2 gap-3">
        {opts.map((o) => (
          <button
            key={o.word}
            onClick={() => tap(o)}
            disabled={!!feedback}
            className={`p-6 rounded-3xl shadow-lg transition-transform hover:scale-105 ${
              feedback === "right" && o.word === target.word ? "bg-sage-light ring-4 ring-sage-light" :
              feedback === "wrong" && o.word !== target.word && opts.find((x) => x.word === target.word) ? "bg-rust-light" :
              "bg-white dark:bg-ink-800 border-2 border-ink-100 hover:border-gold-500"
            }`}
          >
            <span className="text-6xl">{o.emoji}</span>
          </button>
        ))}
      </div>
      {feedback === "right" && <p className="text-sage-dark font-bold">✅ Yes! "{target.word}"</p>}
      {feedback === "wrong" && <p className="text-rust-dark font-bold">❌ It was "{target.word}" {target.emoji}</p>}
    </div>
  );
}

// ============================================================
// GAME 5: SPELLING BEE — hear word, type it
// ============================================================
export function SpellingBee({ onFinish }) {
  const [round, setRound] = useState(0);
  const [target, setTarget] = useState(() => pick(OBJECTS.filter((o) => o.kid)));
  const [input, setInput] = useState("");
  const [score, setScore] = useState(0);
  const [feedback, setFeedback] = useState(null);
  const TOTAL = 5;

  useEffect(() => { setTimeout(() => speak(target.word), 300); }, [target]);

  function check() {
    const isRight = input.trim().toLowerCase() === target.word.toLowerCase();
    setFeedback(isRight ? "right" : "wrong");
    if (isRight) {
      setScore((s) => s + 1);
      try { playCorrect(); } catch {}
    } else {
      try { playWrong(); } catch {}
    }
    setTimeout(() => {
      const next = round + 1;
      if (next >= TOTAL) {
        earnXP(score * 12, "game");
        onFinish?.(score + (isRight ? 1 : 0));
      } else {
        setRound(next);
        setTarget(pick(OBJECTS.filter((o) => o.kid && o.word !== target.word)));
        setInput("");
        setFeedback(null);
      }
    }, 1300);
  }

  return (
    <div className="space-y-5 text-center">
      <div className="flex items-center justify-between">
        <span className="text-xs font-bold text-gray-500">Word {round + 1}/{TOTAL}</span>
        <span className="text-xs font-bold text-sage-dark">⭐ {score}</span>
      </div>
      <p className="text-sm text-gray-600 dark:text-gray-400">Listen and spell the word</p>
      <button onClick={() => speak(target.word)} className="inline-flex items-center gap-2 bg-gold-500 hover:bg-gold-500 text-white font-bold px-6 py-4 rounded-full shadow-2xl hover:scale-105 transition text-lg">
        <Volume2 className="w-6 h-6" /> Hear again
      </button>
      <input
        type="text"
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && !feedback && check()}
        disabled={!!feedback}
        autoFocus
        className={`w-full px-5 py-4 text-2xl font-bold text-center rounded-2xl border-4 ${
          feedback === "right" ? "border-sage bg-sage-light" :
          feedback === "wrong" ? "border-rust-light bg-rust-light animate-shake" :
          "border-gold-500 bg-white dark:bg-ink-800 focus:border-gold-500"
        }`}
        placeholder="type here..."
      />
      {feedback === "right" && <p className="text-sage-dark font-black">✅ Perfect! {target.emoji}</p>}
      {feedback === "wrong" && <p className="text-rust-dark font-black">❌ It's spelled: {target.word}</p>}
      <button onClick={check} disabled={!!feedback || !input} className="w-full bg-gradient-to-r from-gold-500 to-gold-600 text-white font-black py-3 rounded-2xl shadow-lg hover:scale-[1.02] transition disabled:opacity-40">
        Check
      </button>
    </div>
  );
}

// ============================================================
// GAME 6: WORD HUNT — find words in grid
// ============================================================
export function WordHunt({ onFinish }) {
  const SIZE = 8;
  const [grid, setGrid] = useState([]);
  const [targets, setTargets] = useState([]);
  const [found, setFound] = useState(new Set());
  const [selecting, setSelecting] = useState(null);
  const [path, setPath] = useState([]);

  function buildGrid() {
    const words = pickN(OBJECTS.filter((o) => o.kid && o.word.length >= 3 && o.word.length <= 6 && !o.word.includes(" ")), 4).map((o) => o.word.toUpperCase());
    const g = Array.from({ length: SIZE }, () => Array(SIZE).fill(""));
    // Place each word horizontally or vertically
    words.forEach((w) => {
      let placed = false;
      for (let attempt = 0; attempt < 50 && !placed; attempt++) {
        const horiz = Math.random() > 0.5;
        const r = Math.floor(Math.random() * (horiz ? SIZE : SIZE - w.length));
        const c = Math.floor(Math.random() * (horiz ? SIZE - w.length : SIZE));
        let canPlace = true;
        for (let i = 0; i < w.length; i++) {
          const cell = horiz ? g[r][c + i] : g[r + i][c];
          if (cell && cell !== w[i]) { canPlace = false; break; }
        }
        if (canPlace) {
          for (let i = 0; i < w.length; i++) {
            if (horiz) g[r][c + i] = w[i];
            else g[r + i][c] = w[i];
          }
          placed = true;
        }
      }
    });
    // Fill empty with random letters
    for (let r = 0; r < SIZE; r++) for (let c = 0; c < SIZE; c++) {
      if (!g[r][c]) g[r][c] = "ABCDEFGHIJKLMNOPQRSTUVWXYZ"[Math.floor(Math.random() * 26)];
    }
    setGrid(g);
    setTargets(words);
    setFound(new Set());
  }

  useEffect(() => { buildGrid(); }, []);

  function startSel(r, c) {
    setSelecting({ r, c });
    setPath([{ r, c }]);
  }
  function moveSel(r, c) {
    if (!selecting) return;
    setPath((p) => {
      const dr = Math.sign(r - selecting.r);
      const dc = Math.sign(c - selecting.c);
      const newPath = [];
      let cr = selecting.r, cc = selecting.c;
      while (true) {
        newPath.push({ r: cr, c: cc });
        if (cr === r && cc === c) break;
        cr += dr || 0; cc += dc || 0;
        if (newPath.length > 10) break;
      }
      return newPath;
    });
  }
  function endSel() {
    if (!path.length) return;
    const word = path.map((p) => grid[p.r][p.c]).join("");
    if (targets.includes(word) && !found.has(word)) {
      setFound((f) => new Set([...f, word]));
      try { playCorrect(); } catch {}
      speak(word);
      if (found.size + 1 >= targets.length) {
        earnXP(targets.length * 15, "game");
        setTimeout(() => onFinish?.(targets.length), 700);
      }
    } else if (word.length > 1) {
      try { playWrong(); } catch {}
    }
    setSelecting(null);
    setPath([]);
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <span className="text-xs font-bold text-gray-500">Found {found.size}/{targets.length}</span>
        <button onClick={buildGrid} className="text-xs text-gold-700 underline flex items-center gap-1"><RefreshCw className="w-3 h-3" /> New grid</button>
      </div>
      <p className="text-center text-xs text-gray-500">Drag (or tap-tap) to find: <strong className="text-gold-700">{targets.filter((t) => !found.has(t)).join(", ") || "all done!"}</strong></p>

      <div
        className="grid gap-1 mx-auto select-none"
        style={{ gridTemplateColumns: `repeat(${SIZE}, minmax(0, 1fr))`, maxWidth: 380 }}
        onMouseLeave={endSel}
        onMouseUp={endSel}
        onTouchEnd={endSel}
      >
        {grid.flatMap((row, r) =>
          row.map((ch, c) => {
            const inPath = path.some((p) => p.r === r && p.c === c);
            const isFound = [...found].some((w) => {
              return w.includes(ch); // visual hint only
            });
            return (
              <button
                key={`${r}-${c}`}
                onMouseDown={() => startSel(r, c)}
                onMouseEnter={() => moveSel(r, c)}
                onTouchStart={() => startSel(r, c)}
                className={`aspect-square text-base font-black rounded-lg transition ${
                  inPath ? "bg-gold-500 text-gold-700 scale-110" :
                  isFound ? "bg-sage-light text-sage-dark" :
                  "bg-white dark:bg-ink-800 text-gray-800 dark:text-gray-200 border border-gray-200 dark:border-gray-700 hover:bg-paper-deep"
                }`}
              >
                {ch}
              </button>
            );
          })
        )}
      </div>

      <div className="flex flex-wrap gap-2 justify-center">
        {targets.map((t) => (
          <span key={t} className={`text-xs font-bold px-2 py-1 rounded-full ${found.has(t) ? "bg-sage text-white line-through" : "bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300"}`}>
            {t}
          </span>
        ))}
      </div>
    </div>
  );
}
